import { useQuery } from "@tanstack/react-query";
import { ProgramType, Source, Target } from "api/client.generated";
import useSyncTypeGuard from "hooks/useSyncTypeGuard";
import {
  SyncDigestStatsViewModel,
  SyncDigestStatsViewModelPaged,
} from "types/sync-digest-stats-vm";
import SyncType from "types/sync-type";
import useApi from "./useApi";

interface useSyncHistoryPagedReturn {
  syncHistory: SyncDigestStatsViewModelPaged | undefined;
  isSyncHistoryFetching: boolean;
}

const useSyncHistoryPaged = (
  source: Source,
  target: Target,
  program: ProgramType,
  syncType: SyncType,
  skip: number | undefined,
  take: number | undefined,
): useSyncHistoryPagedReturn => {
  useSyncTypeGuard(syncType);

  const { digestClient } = useApi();

  const loadData = async (): Promise<SyncDigestStatsViewModelPaged> => {
    const response =
      syncType === "Billing"
        ? await digestClient.getCustomerSyncHistory(source, target, program, skip, take)
        : await digestClient.getProductSyncHistory(source, target, program, skip, take);

    return new SyncDigestStatsViewModelPaged(
      response?.items.map((x) => new SyncDigestStatsViewModel(x)) ?? [],
      response.paging,
    );
  };

  const { data: syncHistory, isFetching: isSyncHistoryFetching } = useQuery({
    queryKey: ["syncHistory", source, target, program, syncType, skip, take],
    queryFn: loadData,
  });

  return { syncHistory, isSyncHistoryFetching };
};

export default useSyncHistoryPaged;
